import { z } from 'zod';
import { ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

/** Max length of the optional free-text review body (trimmed server-side). */
export const REVIEW_TEXT_MAX = 500;
/** How many recent reviews the public directory projection carries. */
export const RECENT_REVIEWS_LIMIT = 5;
/** Cap on the anonymised author display name (a first name only). */
export const REVIEW_AUTHOR_NAME_MAX = 60;

/**
 * A post-wash review — TEXT ONLY, no photos ever (owner privacy decision).
 *
 * Stored at `tenants/{tid}/outlets/{oid}/reviews/{ticketId}`: the doc id EQUALS
 * the source `ticketId`, so a customer has exactly ONE review per collected
 * wash (re-submitting edits the same doc). Written only by the `submitReview` /
 * `setReviewHidden` callables (admin SDK) — clients never write directly
 * (firestore.rules deny). The public `find` directory never reads this doc; it
 * sees only the anonymised projection on the `outletDirectory` row.
 */
export const ReviewSchema = z.object({
  id: ShortIdSchema.describe('Doc id — equals the source ticketId (one review per wash)'),
  customerUid: UidSchema.describe(
    'Author uid — PRIVATE; used for ownership + read-rule checks, NEVER projected to the public directory',
  ),
  authorName: z
    .string()
    .max(REVIEW_AUTHOR_NAME_MAX)
    .describe(
      'Anonymised display name derived server-side: best-effort FIRST name only, or "" when unknown (UI renders a localised "A customer" fallback). Never the full name, never the uid.',
    ),
  rating: z.number().int().min(1).max(5).describe('Star rating, 1..5 (integer)'),
  text: z
    .string()
    .max(REVIEW_TEXT_MAX)
    .optional()
    .describe('Optional free-text body, trimmed, ≤500 chars. No photos, no PII prompts.'),
  ticketId: ShortIdSchema.describe('The collected wash this review is for (equals the doc id)'),
  outletId: z.string().min(1).describe('Denormalised outlet id (drives the aggregation trigger)'),
  createdAt: TimestampSchema.describe('When the review was first submitted'),
  updatedAt: TimestampSchema.describe('When the review was last edited'),
  hidden: z
    .boolean()
    .default(false)
    .describe(
      'Operator moderation flag. Hidden reviews are excluded from the rating aggregate AND from the public display; the author can still see + edit their own hidden review.',
    ),
});
export type Review = z.infer<typeof ReviewSchema>;

/**
 * Anonymised public snapshot of a recent review — carried on the public
 * `outletDirectory` row and the private `reviewStats` doc. Contains NO uid and
 * NO ticketId: only what the public directory detail page renders.
 */
export const RecentReviewSchema = z.object({
  rating: z.number().int().min(1).max(5).describe('Star rating, 1..5'),
  text: z
    .string()
    .max(REVIEW_TEXT_MAX)
    .optional()
    .describe('Optional body; omitted when the review had none'),
  authorName: z
    .string()
    .max(REVIEW_AUTHOR_NAME_MAX)
    .describe('First name only, or "" → UI shows a localised "A customer"'),
  at: TimestampSchema.describe('When the review was submitted (drives the relative-date label)'),
});
export type RecentReview = z.infer<typeof RecentReviewSchema>;

/**
 * Private per-outlet rating aggregate at
 * `tenants/{tid}/outlets/{oid}/reviewStats/current`. The single source of truth
 * for the outlet's rating: maintained incrementally (delta) by the
 * `onReviewWritten` trigger and projected onto the public `outletDirectory` row
 * (`rating` = `avgRating`, `ratingCount` = `reviewCount`, plus `recentReviews`).
 * Counts NON-hidden reviews only.
 */
export const ReviewStatsSchema = z.object({
  ratingSum: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Sum of non-hidden ratings; delta-maintained (integer → no float drift)'),
  reviewCount: z
    .number()
    .int()
    .nonnegative()
    .default(0)
    .describe('Count of non-hidden reviews behind the average'),
  avgRating: z
    .number()
    .min(0)
    .max(5)
    .default(0)
    .describe('ratingSum / reviewCount, or 0 when reviewCount is 0'),
  recentReviews: z
    .array(RecentReviewSchema)
    .max(RECENT_REVIEWS_LIMIT)
    .default([])
    .describe('Latest non-hidden reviews, newest first, for public display'),
  updatedAt: TimestampSchema.describe('Last aggregation write'),
});
export type ReviewStats = z.infer<typeof ReviewStatsSchema>;
